// =============================================================================
// App.js - Smart Home Frontend v5.0
// =============================================================================
// Componente principal: AC + Câmaras + Estores + Bomba + VMC + Gates + Auth
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wind,
  RefreshCw, 
  LogIn, 
  AlertCircle,
  Wrench,
  Camera,
  ArrowLeft, 
  Blinds, 
  ChevronRight,
  Flame, 
  Fan, 
  Activity,
  Settings,
  Clock,
  DoorOpen,
  Shield,
  Building2,
  Sun,
  Users,
  Lightbulb,
  Utensils,
  WashingMachine,
  Bot,
  Tv as TvIcon,
  Bell,
  ShieldCheck
} from 'lucide-react';

// Componentes
import Dashboard from './Dashboard';
import SettingsComponent from './Settings';
import Scheduler from './Scheduler';
import Gates from './Gates';
import AuthLogs from './AuthLogs';
import Logs from './Logs';
import Firmware from './Firmware';
import Notifications from './Notifications';
import LanguageSwitcher from './LanguageSwitcher';
import MfaStep from './MfaStep';
import Security from './views/Security';
import HeatPump from './views/HeatPump';
import Vmc from './views/Vmc';
import RgbLed from './views/RgbLed';
import Dishwasher from './views/Dishwasher';
import Hood from './views/Hood';
import Tv from './views/Tv';
import Washer from './views/Washer';
import Vacuum from './views/Vacuum';
import BlindsView from './views/BlindsView';
import Cameras from './views/Cameras';
import AirConditioning from './views/AirConditioning';
import { useTranslation } from 'react-i18next';
import TenantManager from './TenantManager';
import Solar from './Solar';
import UserManagement from './UserManagement';

// Serviços
import apiService from './apiService';

// Estilos
import './App.css';
import './Settings.css';
import './Scheduler.css';
import './Gates.css';
import './AuthLogs.css';
import './TenantManager.css';
import './Solar.css';
import './UserManagement.css';


// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

function App() {
  const { t } = useTranslation();
  // ==================== ESTADOS ====================
  
  // Autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Password certa, segundo fator por dar: { modo, challenge } ou null
  const [mfaPendente, setMfaPendente] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  
  // Multi-Tenant
  const [availableTenants, setAvailableTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenantSelected, setTenantSelected] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  
  // Navegação
  const [currentView, setCurrentView] = useState('menu');
  
  // Formulário de login
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // MELCloud (AC)
  const [contextKey, setContextKey] = useState('');
  
  // Câmaras
  const [cameras, setCameras] = useState([]);
  
  // Estores (Shelly)
  
  // Tuya (Bomba de Calor + VMC)

  // Home Connect (Máquina de Loiça + Exaustor)

  // LG ThinQ (Máquina de Lavar Roupa)

  // TV LG (WebOS, rede local)

  // Menu: secção de administração recolhida por omissão
  const [adminExpanded, setAdminExpanded] = useState(false);

  // API URL
  const API_URL = '/api';

  // Helper para headers de autenticação
  const getAuthHeaders = useCallback(() => {
    const token = accessToken || localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, [accessToken]);


  // ==================== AUTENTICAÇÃO ====================
  
  // Início de sessão propriamente dito. Chamado pelo login quando o backend
  // devolve logo tokens, ou pelo MfaStep depois do segundo fator.
  const finalizarLogin = (response) => {
    setMfaPendente(null);
    setPassword('');
    // Sem token não há sessão. O frontend anterior dava-a por iniciada na
    // mesma e mostrava "Nenhuma instalação disponível" (o pedido seguinte
    // levava 401) - foi o que aconteceu a quem tinha o separador aberto
    // quando o MFA entrou. Mais vale um erro claro do que um ecrã vazio.
    if (!response?.access_token) {
      setError(t('erros.autenticar'));
      return;
    }
    if (response.user) {
      setCurrentUser(response.user);
    }
    if (response.access_token) {
      setAccessToken(response.access_token);
      localStorage.setItem('access_token', response.access_token);
      // Só access_token: havia uma segunda chave 'token' que o refresh
      // do apiService nunca atualizava, e os ecrãs que a liam passavam a
      // usar um token expirado depois da primeira renovação.
    }
    setIsAuthenticated(true);
    setTenantSelected(false);
    setError('');
  };

  // O desafio morreu (expirou, esgotou tentativas) ou o utilizador quis
  // voltar atrás: regressa ao ecrã da password.
  const reiniciarLogin = (mensagem) => {
    setMfaPendente(null);
    setPassword('');
    setError(mensagem || '');
  };

  const handleAuth = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.login(username, password);

      // Password certa mas falta o segundo fator: ainda não há sessão.
      if (response.mfa_required) {
        setMfaPendente({ modo: 'verificar', challenge: response.challenge });
        return;
      }
      if (response.mfa_setup_required) {
        setMfaPendente({ modo: 'configurar', challenge: response.challenge });
        return;
      }

      finalizarLogin(response);
    } catch (err) {
      setError(err.message || t('erros.autenticar'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = accessToken || localStorage.getItem('access_token');
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (err) {
      // Silenciar erro de logout
    } finally {
      setIsAuthenticated(false);
      setTenantSelected(false);
      setCurrentTenant(null);
      setAvailableTenants([]);
      setCurrentView('menu');
      setContextKey('');
      setCurrentUser(null);
      setAccessToken('');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      localStorage.removeItem('current_tenant_id');
    }
  };


  // ==================== MULTI-TENANT ====================
  
  const fetchAvailableTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const response = await fetch(`${API_URL}/tenants/my`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const tenants = Array.isArray(data) ? data : (data.tenants || []);
        setAvailableTenants(tenants);
        
        // Se só tem uma instalação, selecionar automaticamente
        if (tenants.length === 1) {
          await selectTenant(tenants[0]);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar instalações:', err);
    } finally {
      setLoadingTenants(false);
    }
  }, [getAuthHeaders]);

  const selectTenant = async (tenant) => {
    try {
      const response = await fetch(`${API_URL}/tenants/switch/${tenant.id}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      setCurrentTenant(tenant);
      setTenantSelected(true);
      localStorage.setItem('current_tenant_id', tenant.id);
      
      // Limpar dados anteriores ao trocar de instalação
      setCameras([]);
      // A lista Tuya vive agora dentro de cada vista, que é destruída ao sair
      // dela - não há cache para limpar aqui.
    } catch (err) {
      console.error('Erro ao selecionar instalação:', err);
      // Permitir continuar mesmo com erro
      setCurrentTenant(tenant);
      setTenantSelected(true);
    }
  };

  // Carregar tenants após login
  useEffect(() => {
    if (isAuthenticated && !tenantSelected) {
      fetchAvailableTenants();
    }
  }, [isAuthenticated, tenantSelected, fetchAvailableTenants]);



  // ==================== CÂMARAS ====================
  
  const fetchCameras = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/cameras`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.cameras && Array.isArray(data.cameras)) {
        setCameras(data.cameras);
      }
    } catch (err) {
      // Silenciar erro
    }
  }, [getAuthHeaders]);


  // Buscar câmaras no início (para badge do menu)
  useEffect(() => {
    if (isAuthenticated) {
      fetchCameras();
    }
  }, [fetchCameras, isAuthenticated]);




  // =============================================================================
  // RENDERS
  // =============================================================================

  // ==================== SEGUNDO FATOR ====================
  if (!isAuthenticated && mfaPendente) {
    return (
      <MfaStep
        modo={mfaPendente.modo}
        challenge={mfaPendente.challenge}
        onDone={finalizarLogin}
        onRestart={reiniciarLogin}
      />
    );
  }

  // ==================== TELA DE LOGIN ====================
  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <img src="/logo192.png" alt="Smart Home" className="brand-logo" />
            <div>
              <h1>Smart Home</h1>
              <p>{t('login.subtitulo')}</p>
            </div>
          </div>
          <div className="login-form">
            <div className="form-group">
              <label>{t('login.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
              />
            </div>
            <div className="form-group">
              <label>{t('login.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
              />
            </div>
            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            <button onClick={handleAuth} className="login-button" disabled={loading}>
              <LogIn size={20} />
              {loading ? t('login.aEntrar') : t('login.entrar')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SELETOR DE INSTALAÇÃO ====================
  if (isAuthenticated && !tenantSelected) {
    return (
      <div className="login-container">
        <div className="login-box tenant-selector">
          <div className="login-header">
            <Building2 size={32} />
            <div>
              <h1>{t('instalacoes.titulo')}</h1>
              <p>{t('instalacoes.escolher')}</p>
            </div>
          </div>
          
          {loadingTenants ? (
            <div className="tenant-loading">
              <RefreshCw size={32} className="spin" />
              <p>{t('instalacoes.aCarregar')}</p>
            </div>
          ) : availableTenants.length === 0 ? (
            <div className="tenant-empty">
              <AlertCircle size={32} />
              <p>{t('instalacoes.nenhuma')}</p>
            </div>
          ) : (
            <div className="tenant-list-select">
              {availableTenants.map(tenant => (
                <div 
                  key={tenant.id} 
                  className="tenant-option"
                  onClick={() => selectTenant(tenant)}
                >
                  {tenant.photo_url ? (
                    <img
                      src={tenant.photo_url}
                      alt={tenant.name}
                      className="tenant-option-photo"
                    />
                  ) : (
                    <div className="tenant-option-icon">
                      <Building2 size={24} />
                    </div>
                  )}
                  <div className="tenant-option-info">
                    <h3>{tenant.name}</h3>
                    <span>{tenant.enabled_modules?.length || 0} módulos ativos</span>
                  </div>
                  <ChevronRight size={20} className="tenant-option-arrow" />
                </div>
              ))}
            </div>
          )}
          
          <button onClick={handleLogout} className="logout-button">
            {t('menu.terminarSessao')}
          </button>
        </div>
      </div>
    );
  }

  // ==================== MENU PRINCIPAL ====================
  if (currentView === 'menu') {
    // Função helper para verificar se módulo está ativo
    const isModuleEnabled = (moduleName) => {
      if (!currentTenant?.enabled_modules) return false;
      return currentTenant.enabled_modules.includes(moduleName);
    };
    
    // Verificar se é super_admin
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Menu agrupado por FUNÇÃO, não por marca: o utilizador pensa "quero
    // acender a luz da sala", não "quero controlar o meu dispositivo Tuya".
    // A marca é detalhe de implementação nosso (se a bomba de calor mudar
    // de Tuya para outra coisa, o menu não tem de ser reorganizado).
    // As secções vivem todas no mesmo ecrã - sem submenus, que
    // acrescentariam um toque a todas as ações do dia-a-dia. É o mesmo
    // padrão do Apple Home / Google Home.
    const menuSections = [
      {
        title: t('menu.seccoes.climatizacao'),
        items: [
          {
            show: isModuleEnabled('ac'),
            icon: <Wind size={30} />,
            title: t('menu.arCondicionado'),
            badge: t('menu.badges.mitsubishi'),
            onClick: () => { setCurrentView('ac'); setError(''); }
          },
          { show: isModuleEnabled('heatpump'), icon: <Flame size={30} />, title: t('menu.bombaCalor'), badge: t('menu.badges.aguasQuentes'), onClick: () => setCurrentView('heatpump') },
          { show: isModuleEnabled('vmc'), icon: <Fan size={30} />, title: t('menu.vmc'), badge: t('menu.badges.ventilacao'), onClick: () => setCurrentView('vmc') }
        ]
      },
      {
        title: t('menu.seccoes.casa'),
        items: [
          { show: isModuleEnabled('blinds'), icon: <Blinds size={30} />, title: t('menu.estores'), badge: t('menu.badges.janelas'), onClick: () => setCurrentView('blinds') },
          { show: isModuleEnabled('gates'), icon: <DoorOpen size={30} />, title: t('menu.portoes'), badge: t('menu.badges.acessos'), onClick: () => setCurrentView('gates') },
          { show: isModuleEnabled('leds'), icon: <Lightbulb size={30} />, title: t('menu.luzesRgb'), badge: t('menu.badges.sala'), onClick: () => setCurrentView('rgbled') }
        ]
      },
      {
        title: t('menu.seccoes.eletrodomesticos'),
        items: [
          { show: isModuleEnabled('homeconnect'), icon: <Utensils size={30} />, title: t('menu.maquinaLoica'), badge: t('menu.badges.bosch'), onClick: () => setCurrentView('dishwasher') },
          { show: isModuleEnabled('homeconnect'), icon: <Wind size={30} />, title: t('menu.exaustor'), badge: t('menu.badges.bosch'), onClick: () => setCurrentView('hood') },
          { show: isModuleEnabled('lgthinq'), icon: <WashingMachine size={30} />, title: t('menu.maquinaRoupa'), badge: t('menu.badges.lg'), onClick: () => setCurrentView('lgthinq') },
          { show: isModuleEnabled('roborock'), icon: <Bot size={30} />, title: t('menu.aspirador'), badge: t('menu.badges.roborock'), onClick: () => setCurrentView('vacuum') }
        ]
      },
      {
        title: t('menu.seccoes.multimediaSeguranca'),
        items: [
          { show: isModuleEnabled('lgtv'), icon: <TvIcon size={30} />, title: t('menu.tv'), badge: t('menu.badges.sala'), onClick: () => setCurrentView('lgtv') },
          { show: isModuleEnabled('cameras'), icon: <Camera size={30} />, title: t('menu.videovigilancia'), badge: t('menu.badges.camaras', { count: cameras.length }), onClick: () => setCurrentView('cameras') }
        ]
      },
      {
        title: t('menu.seccoes.energiaAnalise'),
        items: [
          { show: isModuleEnabled('solar'), icon: <Sun size={30} />, title: t('menu.energiaSolar'), badge: t('menu.badges.sungrow'), onClick: () => setCurrentView('solar') },
          { show: currentTenant?.enabled_modules?.length > 0, icon: <Activity size={30} />, title: t('menu.dashboard'), badge: t('menu.badges.historico'), onClick: () => setCurrentView('dashboard') }
        ]
      },
      {
        title: t('menu.seccoes.automacao'),
        items: [
          { show: currentTenant?.enabled_modules?.length > 0, icon: <Clock size={30} />, title: t('menu.agendamentos'), badge: t('menu.badges.programarAcoes'), onClick: () => setCurrentView('scheduler') },
          { show: true, icon: <Bell size={30} />, title: t('menu.notificacoes'), badge: t('menu.badges.avisosTelemovel'), onClick: () => setCurrentView('notifications') },
          { show: true, icon: <ShieldCheck size={30} />, title: t('menu.seguranca'), badge: t('menu.badges.doisPassos'), onClick: () => setCurrentView('security') }
        ]
      },
      {
        // Recolhida por omissão: é usada raramente e só por admins, não faz
        // sentido competir por espaço com os controlos do dia-a-dia.
        title: t('menu.seccoes.administracao'),
        collapsible: true,
        items: [
          { show: currentUser?.role === 'admin' || isSuperAdmin, icon: <Settings size={30} />, title: t('menu.configuracoes'), badge: t('menu.badges.dispositivos'), onClick: () => setCurrentView('settings') },
          { show: isSuperAdmin, icon: <Building2 size={30} />, title: t('menu.instalacoes'), badge: t('menu.badges.multiCasa'), onClick: () => setCurrentView('tenants') },
          { show: isSuperAdmin, icon: <Users size={30} />, title: t('menu.utilizadores'), badge: t('menu.badges.contas'), onClick: () => setCurrentView('users') },
          { show: isSuperAdmin, icon: <Shield size={30} />, title: t('menu.registos'), badge: t('menu.badges.atividade'), onClick: () => setCurrentView('logs') },
          { show: isSuperAdmin, icon: <Wrench size={30} />, title: t('menu.firmware'), badge: t('menu.badges.atualizacoes'), onClick: () => setCurrentView('firmware') }
        ]
      }
    ];
    
    return (
      <div className="menu-container">
        <div className="menu-content">
          <div className="menu-header">
            <img src="/logo192.png" alt="Smart Home" className="brand-logo brand-logo-lg" />
            <h1>Smart Home</h1>
            <p>{t('menu.escolher')}</p>
            {currentUser && (
              <span className="user-badge">
                👤 {currentUser.username} {currentUser.role === 'super_admin' ? t('menu.papelSuperAdmin') : currentUser.role === 'admin' ? t('menu.papelAdmin') : ''}
              </span>
            )}
            {currentTenant && (
              <span 
                className="tenant-badge"
                onClick={() => setTenantSelected(false)}
                title={t('menu.trocarInstalacao')}
              >
                🏠 {currentTenant.name}
              </span>
            )}
            <div className="menu-lang"><LanguageSwitcher /></div>
          </div>

          {menuSections.map(section => {
            const items = section.items.filter(item => item.show);
            if (items.length === 0) return null;

            const collapsed = section.collapsible && !adminExpanded;

            return (
              <div className="menu-section" key={section.title}>
                <button
                  type="button"
                  className={`menu-section-title ${section.collapsible ? 'clickable' : ''}`}
                  onClick={section.collapsible ? () => setAdminExpanded(v => !v) : undefined}
                >
                  <span>{section.title}</span>
                  {section.collapsible && (
                    <ChevronRight size={14} className={adminExpanded ? 'rotated' : ''} />
                  )}
                </button>

                {!collapsed && (
                  <div className="menu-grid">
                    {items.map(item => (
                      <div className="menu-card" key={item.title} onClick={item.onClick}>
                        {item.icon}
                        <h2>{item.title}</h2>
                        <span className="menu-badge">{item.badge}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button onClick={handleLogout} className="logout-button">
            {t('menu.terminarSessao')}
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'heatpump') {
    return <HeatPump onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'dishwasher') {
    return <Dishwasher onBack={() => setCurrentView('menu')} />;
  }
  // ==================== EXAUSTOR (Home Connect) ====================
  if (currentView === 'hood') {
    return <Hood onBack={() => setCurrentView('menu')} />;
  }

  if (currentView === 'lgtv') {
    return <Tv onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'vacuum') {
    return <Vacuum onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'lgthinq') {
    return <Washer onBack={() => setCurrentView('menu')} />;
  }

  if (currentView === 'vmc') {
    return <Vmc onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'rgbled') {
    return <RgbLed onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'blinds') {
    return <BlindsView onBack={() => setCurrentView('menu')} />;
  }

  // ==================== CÂMARAS ====================
  if (currentView === 'cameras') {
    return (
      <Cameras authHeaders={getAuthHeaders} onBack={() => setCurrentView('menu')} />
    );
  }

  // ==================== AC ====================
  if (currentView === 'ac') {
    return <AirConditioning onBack={() => setCurrentView('menu')} />;
  }

  // ==================== DASHBOARD ====================
  if (currentView === 'dashboard') {
    return (
      <div className="app-container">
        <div className="app-content">
          <div className="view-header">
            <button onClick={() => setCurrentView('menu')} className="btn-back">
              <ArrowLeft size={24} />
            </button>
            <div className="view-header-content">
              <div className="view-header-title">
                <Activity size={32} />
                <h1>Dashboard</h1>
              </div>
              <span className="view-subtitle">{t('dashboard.subtituloVista')}</span>
            </div>
          </div>

          <Dashboard apiUrl={API_URL} />
        </div>
      </div>
    );
  }

  // ==================== SETTINGS ====================
  if (currentView === 'settings') {
    return (
      <SettingsComponent 
        apiUrl={API_URL} 
        onBack={() => setCurrentView('menu')} 
      />
    );
  }

  // ==================== SCHEDULER ====================
  if (currentView === 'scheduler') {
    return (
      <Scheduler 
        apiUrl={API_URL} 
        onBack={() => setCurrentView('menu')} 
      />
    );
  }

  // ==================== GATES ====================
  if (currentView === 'gates') {
    return (
      <Gates 
        apiUrl={API_URL} 
        onBack={() => setCurrentView('menu')} 
      />
    );
  }

  // ==================== AUTH LOGS ====================
  if (currentView === 'security') {
    return <Security onBack={() => setCurrentView('menu')} />;
  }
  if (currentView === 'notifications') {
    return (
      <Notifications
        apiUrl={API_URL}
        onBack={() => setCurrentView('menu')}
        token={accessToken || localStorage.getItem('access_token')}
      />
    );
  }

  if (currentView === 'firmware') {
    return (
      <Firmware
        apiUrl={API_URL}
        onBack={() => setCurrentView('menu')}
        token={accessToken || localStorage.getItem('access_token')}
      />
    );
  }

  if (currentView === 'logs') {
    return (
      <Logs
        apiUrl={API_URL}
        onBack={() => setCurrentView('menu')}
        token={accessToken || localStorage.getItem('access_token')}
      />
    );
  }

  // Mantido: o ecrã dedicado de autenticação continua acessível
  // diretamente, embora o menu passe agora pelo ecrã de Registos
  if (currentView === 'auth-logs') {
    return (
      <AuthLogs
        apiUrl={API_URL}
        onBack={() => setCurrentView('menu')}
        token={accessToken || localStorage.getItem('access_token')}
      />
    );
  }

  // ==================== SOLAR ====================
  if (currentView === 'solar') {
    return (
      <Solar 
        apiUrl={API_URL} 
        onBack={() => setCurrentView('menu')} 
      />
    );
  }

  // ==================== TENANT MANAGER ====================
  if (currentView === 'tenants') {
    return (
      <div className="app-container">
        <div className="app-content">
          <div className="view-header">
            <button onClick={() => setCurrentView('menu')} className="btn-back">
              <ArrowLeft size={24} />
            </button>
            <div className="view-header-content">
              <div className="view-header-title">
                <Building2 size={32} />
                <h1>{t('menu.instalacoes')}</h1>
              </div>
              <span className="view-subtitle">{t('menu.badges.multiCasa')}</span>
            </div>
          </div>

          <TenantManager />
        </div>
      </div>
    );
  }

  // ==================== USER MANAGEMENT ====================
  if (currentView === 'users') {
    return (
      <UserManagement 
        apiUrl={API_URL}
        currentUsername={currentUser?.username}
        onBack={() => setCurrentView('menu')}
      />
    );
  }

  return null;
}

export default App;
